/**
 * Remembers the sidebar the way the user left it, per browser: collapsed or
 * expanded (desktop only), and which menu items are pinned.
 *
 * Below DESKTOP_MIN_WIDTH the sidebar is an off-canvas drawer that always
 * starts closed — an open drawer covers the page — so the saved choice only
 * applies on desktop, and automatic closes on small screens never overwrite
 * it. Storage can be unavailable (private mode, blocked storage), so every
 * read/write is best-effort and falls back to the defaults.
 */

const COLLAPSED_KEY = 'adminv2.sidebar.collapsed';
const PINNED_KEY = 'adminv2.sidebar.pinned';

/** Matches Layout's compact-wrapper breakpoint (and the theme's). */
export const DESKTOP_MIN_WIDTH = 1200;

export const isDesktop = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH;

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the choice just isn't remembered */
  }
};

/** The saved desktop choice, or null if the user never chose. */
export const loadCollapsed = (): boolean | null => {
  const v = read(COLLAPSED_KEY);
  return v === '1' ? true : v === '0' ? false : null;
};

export const saveCollapsed = (collapsed: boolean): void => write(COLLAPSED_KEY, collapsed ? '1' : '0');

/** Collapsed state to start with on the current screen size. */
export const initialCollapsed = (): boolean => (isDesktop() ? loadCollapsed() ?? false : true);

/**
 * Pinned menu titles. The layout keeps a leading "" sentinel in this list
 * (the "Pinned" heading shows when there's more than one entry), so it's
 * restored here too.
 */
export const loadPinned = (): string[] => {
  try {
    const parsed = JSON.parse(read(PINNED_KEY) || '[]');
    if (Array.isArray(parsed)) {
      return ['', ...parsed.filter((t): t is string => typeof t === 'string' && t.length > 0)];
    }
  } catch {
    /* corrupt value — start clean */
  }
  return [''];
};

export const savePinned = (pinned: string[]): void => write(PINNED_KEY, JSON.stringify(pinned.filter(Boolean)));
